import type { SectionType } from "./sections";

/** Editor-facing metadata (Indonesian). Pure data; no rendering concerns. */
export type SectionGroup = "struktur" | "konten" | "bisnis" | "konversi";

export interface SectionMeta {
  label: string;
  description: string;
  group: SectionGroup;
  /** Singletons can appear at most once per page and cannot be added from the picker. */
  singleton?: boolean;
  /** Whether the section is shown in navigation by default when added. */
  navDefault: boolean;
  variantLabels: Record<string, string>;
}

export const SECTION_GROUP_LABELS: Record<SectionGroup, string> = {
  struktur: "Struktur",
  konten: "Konten",
  bisnis: "Bisnis",
  konversi: "Konversi",
};

export const SECTION_META: Record<SectionType, SectionMeta> = {
  navbar: {
    label: "Navigasi",
    description: "Logo dan menu di bagian atas.",
    group: "struktur",
    singleton: true,
    navDefault: false,
    variantLabels: { simple: "Sederhana", centered: "Tengah", floating: "Melayang" },
  },
  hero: {
    label: "Hero",
    description: "Kesan pertama: judul besar, foto, dan tombol utama.",
    group: "konten",
    navDefault: false,
    variantLabels: {
      split: "Terbagi",
      centered: "Tengah",
      "image-background": "Foto latar",
      minimal: "Minimal",
      arch: "Lengkung",
      editorial: "Editorial",
      profile: "Profil",
      celebration: "Pesta",
      cover: "Sampul undangan",
      marquee: "Berjalan",
      collage: "Kolase",
      stage: "Panggung",
      margin: "Bingkai",
      envelope: "Amplop",
      "boarding-pass": "Boarding pass",
      newspaper: "Koran",
      glasshouse: "Rumah kaca",
      slideshow: "Slide foto",
    },
  },
  about: {
    label: "Tentang",
    description: "Cerita singkat usaha Anda.",
    group: "konten",
    navDefault: true,
    variantLabels: { split: "Terbagi", centered: "Tengah", feature: "Sorotan" },
  },
  services: {
    label: "Layanan",
    description: "Daftar layanan beserta harga.",
    group: "bisnis",
    navDefault: true,
    variantLabels: { cards: "Kartu", list: "Daftar", alternating: "Zig-zag", index: "Indeks", chips: "Label" },
  },
  menu: {
    label: "Menu",
    description: "Menu makanan & minuman per kategori.",
    group: "bisnis",
    navDefault: true,
    variantLabels: { cards: "Kartu", list: "Daftar" },
  },
  products: {
    label: "Produk",
    description: "Katalog produk: pesan lewat WhatsApp, atau jual produk digital dari Toko dengan pembayaran online.",
    group: "bisnis",
    navDefault: true,
    variantLabels: { grid: "Grid", cards: "Kartu", tracks: "Daftar lagu" },
  },
  gallery: {
    label: "Galeri",
    description: "Kumpulan foto usaha Anda.",
    group: "konten",
    navDefault: true,
    variantLabels: {
      grid: "Grid",
      mosaic: "Mozaik",
      carousel: "Geser",
      masonry: "Masonry",
      polaroid: "Polaroid",
      slideshow: "Slide",
    },
  },
  testimonials: {
    label: "Testimoni",
    description: "Ulasan asli dari pelanggan.",
    group: "konten",
    navDefault: false,
    variantLabels: { cards: "Kartu", single: "Sorotan", carousel: "Geser", chat: "Obrolan" },
  },
  faq: {
    label: "FAQ",
    description: "Pertanyaan yang sering ditanyakan.",
    group: "konten",
    navDefault: true,
    variantLabels: { accordion: "Akordeon", "two-column": "Dua kolom" },
  },
  stats: {
    label: "Angka",
    description: "Pencapaian dalam angka.",
    group: "konten",
    navDefault: false,
    variantLabels: { row: "Baris", cards: "Kartu" },
  },
  "opening-hours": {
    label: "Jam Buka",
    description: "Jadwal buka dari info bisnis.",
    group: "bisnis",
    navDefault: false,
    variantLabels: { table: "Tabel", card: "Kartu" },
  },
  location: {
    label: "Lokasi",
    description: "Alamat, peta, dan petunjuk arah.",
    group: "bisnis",
    navDefault: true,
    variantLabels: { card: "Kartu", map: "Peta" },
  },
  contact: {
    label: "Kontak",
    description: "WhatsApp, telepon, email, dan media sosial.",
    group: "konversi",
    navDefault: true,
    variantLabels: { "whatsapp-card": "Kartu WhatsApp", split: "Terbagi" },
  },
  pricing: {
    label: "Harga Paket",
    description: "Paket harga untuk dibandingkan.",
    group: "bisnis",
    navDefault: true,
    variantLabels: { cards: "Kartu", table: "Tabel" },
  },
  cta: {
    label: "Ajakan",
    description: "Dorongan terakhir untuk menghubungi Anda.",
    group: "konversi",
    navDefault: false,
    variantLabels: {
      banner: "Banner",
      split: "Terbagi",
      spotlight: "Sorotan",
      celebration: "Pesta",
      marquee: "Berjalan",
    },
  },
  timeline: {
    label: "Linimasa",
    description: "Langkah kerja, perjalanan usaha, atau kisah cinta.",
    group: "konten",
    navDefault: true,
    variantLabels: { vertical: "Vertikal", alternating: "Zig-zag", steps: "Langkah" },
  },
  events: {
    label: "Acara",
    description: "Tanggal, jam, dan tempat acara (akad, resepsi, workshop).",
    group: "bisnis",
    navDefault: true,
    variantLabels: { cards: "Kartu", stacked: "Bertumpuk", agenda: "Agenda" },
  },
  team: {
    label: "Tim & Profil",
    description: "Orang di balik usaha Anda, atau kedua mempelai.",
    group: "konten",
    navDefault: true,
    variantLabels: { grid: "Grid", couple: "Pasangan" },
  },
  quote: {
    label: "Kutipan",
    description: "Kutipan, ayat, atau pernyataan singkat yang menonjol.",
    group: "konten",
    navDefault: false,
    variantLabels: { centered: "Tengah", ornate: "Ornamen" },
  },
  links: {
    label: "Tautan",
    description: "Tombol tautan besar ala link in bio: toko, video, dukungan, kontak.",
    group: "konversi",
    navDefault: false,
    variantLabels: { stack: "Tombol", grid: "Kartu" },
  },
  picks: {
    label: "Rekomendasi & Afiliasi",
    description: "Kartu produk atau proyek dengan tautan keluar (Shopee, TikTok Shop, portofolio).",
    group: "bisnis",
    navDefault: true,
    variantLabels: { grid: "Grid", list: "Daftar", carousel: "Geser", bento: "Bento", showcase: "Etalase", panorama: "Panorama" },
  },
  logos: {
    label: "Logo Mitra",
    description: "Deretan logo klien, mitra, atau media: \"Dipercaya & bekerja sama dengan\".",
    group: "bisnis",
    navDefault: false,
    variantLabels: { slider: "Geser + panah", marquee: "Berjalan", grid: "Dinding logo" },
  },
  videos: {
    label: "Video",
    description: "Video YouTube: satu video utama atau deretan video.",
    group: "konten",
    navDefault: true,
    variantLabels: { featured: "Video utama + daftar", grid: "Grid" },
  },
  lessons: {
    label: "Materi Belajar",
    description: "Materi per pertemuan atau modul: teks, PDF, dan video YouTube.",
    group: "konten",
    navDefault: true,
    variantLabels: { cards: "Kartu pertemuan", list: "Daftar" },
  },
  "product-feature": {
    label: "Produk Unggulan",
    description: "Satu produk digital (e-book, kelas) dengan sampul, harga, isi, dan tombol beli.",
    group: "bisnis",
    navDefault: true,
    variantLabels: { spotlight: "Sorot", split: "Terbagi", stack: "Bertumpuk" },
  },
  countdown: {
    label: "Hitung Mundur",
    description: "Hitungan hari, jam, menit, dan detik menuju hari acara.",
    group: "konten",
    navDefault: false,
    variantLabels: { boxes: "Kotak", minimal: "Minimal" },
  },
  gift: {
    label: "Hadiah & Rekening",
    description: "Nomor rekening atau dompet digital untuk tanda kasih.",
    group: "konversi",
    navDefault: true,
    variantLabels: { cards: "Kartu" },
  },
  donation: {
    label: "Formulir WhatsApp",
    description: "Formulir dengan pertanyaan Anda sendiri (donasi, pendaftaran, pemesanan); jawaban terkirim ke WhatsApp Anda.",
    group: "konversi",
    navDefault: true,
    variantLabels: { card: "Kartu", split: "Terbagi" },
  },
  wishes: {
    label: "Ucapan & Doa",
    description: "Tamu menulis ucapan dan doa yang tampil di undangan.",
    group: "konversi",
    navDefault: true,
    variantLabels: { cards: "Kartu", stream: "Arus" },
  },
  rsvp: {
    label: "RSVP",
    description: "Tamu mengonfirmasi kehadiran dan jumlah orang.",
    group: "konversi",
    navDefault: true,
    variantLabels: { card: "Kartu", split: "Terbagi" },
  },
  "blog-latest": {
    label: "Artikel Terbaru",
    description: "Artikel blog terbaru, otomatis dari menu Blog.",
    group: "konten",
    navDefault: false,
    variantLabels: { cards: "Kartu", list: "Daftar" },
  },
  footer: {
    label: "Footer",
    description: "Informasi di bagian paling bawah.",
    group: "struktur",
    singleton: true,
    navDefault: false,
    variantLabels: { simple: "Sederhana", columns: "Kolom" },
  },
};
